import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCopy, faEye, faHome, faPrint, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Alert, Breadcrumb, Button, Card, Col, Form, Modal, OverlayTrigger, Pagination, Row, Table, Tooltip } from "@themesberg/react-bootstrap";
import { Link, useParams, generatePath } from "react-router-dom";

import { Routes } from "../routes";
import defaultGroups from "../data/groups";
import defaultGroupTransactions from "../data/groupTransactions";
import { BarChart, CircleChart } from "../components/Charts";
import { normalizePhone, parseMpesaMessage } from "../utils/mpesa";
import { apiRequest, getAuthToken } from "../utils/api";
import { formatPersonName, stripMaskedPhoneSuffix } from "../utils/personName";
import ConfirmationToast from "../components/ConfirmationToast";

const GROUPS_STORAGE_KEY = "treasurer.groups";
const TRANSACTIONS_STORAGE_KEY = "treasurer.groupTransactions";
const EXPENSES_STORAGE_KEY = "treasurer.expenses";
const PURPOSES_CACHE_PREFIX = "treasurer.contributionPurposes.";

const loadGroups = () => {
  const stored = localStorage.getItem(GROUPS_STORAGE_KEY);
  if (!stored) {
    return defaultGroups;
  }
  try {
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) {
      return defaultGroups;
    }
    return parsed.map((group) => ({
      ...group,
      frequency: group.frequency || "monthly",
      members: Array.isArray(group.members)
        ? group.members
            .map((member) => {
              if (typeof member === "string") {
                return { id: null, name: member, phone: "", info: "" };
              }
              return {
                id: member?.id ?? null,
                name: member?.name || "",
                phone: member?.phone || "",
                info: member?.info || ""
              };
            })
            .filter((member) => member.name)
        : []
    }));
  } catch (error) {
    return defaultGroups;
  }
};

const loadTransactions = () => {
  const stored = localStorage.getItem(TRANSACTIONS_STORAGE_KEY);
  if (!stored) {
    return defaultGroupTransactions;
  }
  try {
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : defaultGroupTransactions;
  } catch (error) {
    return defaultGroupTransactions;
  }
};

const loadExpenses = () => {
  const stored = localStorage.getItem(EXPENSES_STORAGE_KEY);
  if (!stored) {
    return [];
  }
  try {
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const formatAmount = (amount) => {
  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return "Ksh 0.00";
  }
  return `Ksh ${Number(amount).toLocaleString("en-KE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const formatTableDate = (timestamp = "") => {
  if (!timestamp) {
    return "--";
  }
  const raw = String(timestamp).trim();
  const datePart = raw.split(/\s+/)[0] || "";
  if (!datePart) {
    return "--";
  }
  const pieces = datePart.split(/[/.-]/);
  if (pieces.length < 3) {
    return datePart;
  }
  if (pieces[0].length === 4) {
    return `${pieces[2].padStart(2, "0")}/${pieces[1].padStart(2, "0")}/${pieces[0]}`;
  }
  return datePart;
};

const parseDateValue = (timestamp = "") => {
  if (!timestamp) {
    return null;
  }
  const raw = String(timestamp).trim();
  const datePart = raw.split(/\s+/)[0] || "";
  if (!datePart) {
    return null;
  }
  const pieces = datePart.split(/[/.-]/);
  if (pieces.length < 3) {
    const parsed = new Date(datePart);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (pieces[0].length === 4) {
    const parsed = new Date(`${pieces[0]}-${pieces[1]}-${pieces[2]}`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const day = Number(pieces[0]);
  const month = Number(pieces[1]);
  const year = Number(pieces[2].length === 2 ? `20${pieces[2]}` : pieces[2]);
  const parsed = new Date(year, month - 1, day);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getWeekKey = (date) => {
  const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNumber = utcDate.getUTCDay() || 7;
  utcDate.setUTCDate(utcDate.getUTCDate() + 4 - dayNumber);
  const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1));
  const weekNumber = Math.ceil(((utcDate - yearStart) / 86400000 + 1) / 7);
  return {
    key: `${utcDate.getUTCFullYear()}-W${String(weekNumber).padStart(2, "0")}`,
    weekNumber,
    year: utcDate.getUTCFullYear()
  };
};

const formatReportNumber = (num) => {
  const n = Number(num);
  if (Number.isNaN(n)) {
    return "0";
  }
  const rounded = Math.round(n);
  return rounded.toLocaleString("en-KE");
};

const formatReportAmount = (num) => `Ksh ${formatReportNumber(num)}`;

const numericIdRank = (id) => {
  const n = Number(id);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
};

/**
 * One visible row per M-Pesa confirmation code. Duplicate API rows (same payment saved twice)
 * collapse to the transaction with the lowest numeric id (first inserted).
 */
const dedupeMpesaRowsByConfirmationCode = (list) => {
  const byCode = new Map();
  for (const t of list) {
    if (t.type === "cash") {
      continue;
    }
    const code = t.code != null && String(t.code).trim() ? String(t.code).trim() : "";
    if (!code || /^cash$/i.test(code)) {
      continue;
    }
    const k = code.toLowerCase();
    const prev = byCode.get(k);
    if (!prev || numericIdRank(t.id) < numericIdRank(prev.id)) {
      byCode.set(k, t);
    }
  }
  const out = [];
  for (const t of list) {
    if (t.type === "cash") {
      out.push(t);
      continue;
    }
    const code = t.code != null && String(t.code).trim() ? String(t.code).trim() : "";
    if (!code || /^cash$/i.test(code)) {
      out.push(t);
      continue;
    }
    const winner = byCode.get(code.toLowerCase());
    if (winner && String(winner.id) === String(t.id)) {
      out.push(t);
    }
  }
  return out;
};

/**
 * M-Pesa ref for PUT payloads. Row `code` can be missing/empty on grouped UI; sending null clears DB
 * and the same SMS can be saved again as a duplicate. Fall back to parsing `message`.
 */
const resolveMpesaCodeForPutBody = (transaction) => {
  if (!transaction || transaction.type === "cash") {
    return transaction?.code || "CASH";
  }
  let fromRow =
    transaction.code != null && String(transaction.code).trim() !== "" ? String(transaction.code).trim() : "";
  if (fromRow.includes(",")) {
    fromRow = fromRow.split(",")[0].trim();
  }
  if (fromRow) {
    return fromRow;
  }
  const fromMsg = parseMpesaMessage(transaction.message || "");
  const c = fromMsg.code && String(fromMsg.code).trim() ? String(fromMsg.code).trim() : "";
  return c || null;
};

const sortContributionsByCreatedAsc = (list) =>
  [...list].sort((a, b) => {
    const tA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const tB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return tA - tB;
  });

const loadCachedPurposes = (groupId) => {
  if (!groupId) return [];
  const stored = localStorage.getItem(PURPOSES_CACHE_PREFIX + groupId);
  if (!stored) return [];
  try {
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const buildContributionReport = (group, transactions, expenses, purposeLookup, headerMessage = "") => {
  if (!group) {
    return "Group not found.";
  }
  const totalCollected = transactions.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

  const getPurposeName = (purposeId) => {
    if (purposeId == null) return "Uncategorized";
    const p = purposeLookup?.get(Number(purposeId)) || purposeLookup?.get(String(purposeId));
    return p?.name || "Uncategorized";
  };

  const byPurpose = new Map();
  transactions.forEach((t) => {
    const key = t.purposeId != null ? String(t.purposeId) : "__uncategorized";
    if (!byPurpose.has(key)) {
      byPurpose.set(key, []);
    }
    byPurpose.get(key).push(t);
  });

  const lines = [];

  const headerText = String(headerMessage || "").trim();
  if (headerText) {
    lines.push(headerText);
    lines.push("");
  }

  if (byPurpose.size === 0) {
    lines.push("Uncategorized");
    lines.push("(No contributions)");
    lines.push("");
  }

  const purposeFirstCreatedMs = (key) => {
    const items = byPurpose.get(key) || [];
    let min = Number.MAX_SAFE_INTEGER;
    for (const t of items) {
      if (t.createdAt) {
        const ms = new Date(t.createdAt).getTime();
        if (!Number.isNaN(ms)) min = Math.min(min, ms);
      }
    }
    return min;
  };

  const sortedKeys = Array.from(byPurpose.keys()).sort((a, b) => {
    if (a === "__uncategorized") return 1;
    if (b === "__uncategorized") return -1;
    const tA = purposeFirstCreatedMs(a);
    const tB = purposeFirstCreatedMs(b);
    if (tA !== tB) return tA - tB;
    const nameA = getPurposeName(Number(a));
    const nameB = getPurposeName(Number(b));
    return nameA.localeCompare(nameB);
  });

  sortedKeys.forEach((key) => {
    const items = byPurpose.get(key);
    const purposeId = key === "__uncategorized" ? null : Number(key);
    const purposeName = getPurposeName(purposeId);
    const purposeObj = purposeId != null ? (purposeLookup?.get(purposeId) || purposeLookup?.get(key)) : null;
    const treasurerNum = (purposeObj?.treasurerNumber ?? "").trim();

    // Purpose name and treasurer can be included in the editable header message.
    // Here we only ensure separation between purpose groups.
    if (lines.length > 0 && lines[lines.length - 1] !== "") {
      lines.push("");
    }

    if (items.length === 0) {
      lines.push("(No contributions)");
    } else {
      const reportCreatedMs = (t) => {
        if (t.createdAt) {
          const ms = new Date(t.createdAt).getTime();
          if (!Number.isNaN(ms)) return ms;
        }
        return Number.MAX_SAFE_INTEGER;
      };
      const totalsBySender = new Map();
      items.forEach((t) => {
        const raw = (t.sender || "Unknown").trim() || "Unknown";
        const nameOnly = stripMaskedPhoneSuffix(raw);
        const senderKey = (nameOnly || raw).trim().toLowerCase() || "unknown";
        const amount = Number(t.amount) || 0;
        const label = nameOnly ? formatPersonName(nameOnly) : "Unknown";
        const tMs = reportCreatedMs(t);
        const prev = totalsBySender.get(senderKey);
        if (prev) {
          totalsBySender.set(senderKey, {
            amount: prev.amount + amount,
            label: prev.label || label,
            firstCreatedAt: Math.min(prev.firstCreatedAt, tMs)
          });
        } else {
          totalsBySender.set(senderKey, { amount, label, firstCreatedAt: tMs });
        }
      });
      let n = 1;
      Array.from(totalsBySender.entries())
        .sort((a, b) => a[1].firstCreatedAt - b[1].firstCreatedAt)
        .forEach(([, { amount: totalAmount, label }]) => {
          lines.push(`${n}. ${label}: ${formatReportAmount(totalAmount)}`);
          n += 1;
        });
    }
    lines.push("");
  });

  const hasExpenses = expenses.length > 0;
  if (hasExpenses) {
    lines.push("Expenses:");
    let eIdx = 1;
    const expenseMs = (e) => {
      if (e.createdAt) {
        const ms = new Date(e.createdAt).getTime();
        if (!Number.isNaN(ms)) return ms;
      }
      return Number.MAX_SAFE_INTEGER;
    };
    [...expenses]
      .sort((a, b) => expenseMs(a) - expenseMs(b))
      .forEach((e) => {
        const label = e.category?.name || e.categoryName || "Uncategorized";
        const amount = formatReportAmount(e.amount);
        lines.push(`${eIdx}. ${label}: ${amount}`);
        eIdx += 1;
      });
    lines.push("");
  }

  lines.push(`Total contributions: ${formatReportAmount(totalCollected)}`);

  return lines.join("\n");
};

export default () => {
  const { groupId } = useParams();
  const [groups, setGroups] = useState(loadGroups);
  const [transactions, setTransactions] = useState(loadTransactions);
  const [copied, setCopied] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [expenses, setExpenses] = useState(loadExpenses);
  const [reportHeaderMessage, setReportHeaderMessage] = useState(() => {
    if (!groupId) return "";
    return localStorage.getItem(`treasurer.reportHeaderMessage.${groupId}`) || "";
  });
  const [showCharts, setShowCharts] = useState(false);
  const [contributionPurposes, setContributionPurposes] = useState(() => loadCachedPurposes(groupId));
  const [searchTerm, setSearchTerm] = useState("");
  const [purposeFilterId, setPurposeFilterId] = useState(""); // "" = all, "none" = uncategorized, or purpose id
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [displayMode, setDisplayMode] = useState("grouped"); // "grouped" | "all"
  const [contributionSort, setContributionSort] = useState("date_desc"); // date_desc | name_asc | name_desc
  const [updatingPurposeId, setUpdatingPurposeId] = useState(null);
  const [editingAmountId, setEditingAmountId] = useState(null);
  const [amountEditValue, setAmountEditValue] = useState("");
  const [amountError, setAmountError] = useState("");
  const [updatingAmountId, setUpdatingAmountId] = useState(null);
  const [editingSenderId, setEditingSenderId] = useState(null);
  const [senderEditValue, setSenderEditValue] = useState("");
  const [senderEditError, setSenderEditError] = useState("");
  const [updatingSenderId, setUpdatingSenderId] = useState(null);
  const [editingCodeId, setEditingCodeId] = useState(null);
  const [codeEditValue, setCodeEditValue] = useState("");
  const [codeEditError, setCodeEditError] = useState("");
  const [updatingCodeId, setUpdatingCodeId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [transactionPendingDelete, setTransactionPendingDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [purposeForm, setPurposeForm] = useState({ name: "", description: "", treasurerNumber: "" });
  const [editingPurposeId, setEditingPurposeId] = useState(null);
  const [editingPurposeValues, setEditingPurposeValues] = useState({ name: "", description: "", treasurerNumber: "" });
  const [purposeError, setPurposeError] = useState("");
  const [showPurposesCard, setShowPurposesCard] = useState(false);
  const [toast, setToast] = useState({ show: false, variant: "success", message: "" });

  const showToast = (variant, message) => {
    setToast({ show: true, variant, message });
  };

  const closeToast = () => {
    setToast((prev) => ({ ...prev, show: false }));
  };

  useEffect(() => {
    localStorage.setItem(TRANSACTIONS_STORAGE_KEY, JSON.stringify(transactions));
  }, [transactions]);

  useEffect(() => {
    localStorage.setItem(EXPENSES_STORAGE_KEY, JSON.stringify(expenses));
  }, [expenses]);

  useEffect(() => {
    localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(groups));
  }, [groups]);

  useEffect(() => {
    setContributionPurposes(loadCachedPurposes(groupId));
  }, [groupId]);

  useEffect(() => {
    const token = getAuthToken();
    if (!token || !groupId) return;
    apiRequest(`/groups/${groupId}/contribution-purposes`)
      .then((data) => {
        if (data?.purposes) {
          setContributionPurposes(
            data.purposes.map((p) => ({
              id: String(p.id),
              name: p.name,
              description: p.description || "",
              treasurerNumber: p.treasurerNumber || ""
            }))
          );
        }
      })
      .catch(() => {});
  }, [groupId]);

  useEffect(() => {
    if (!groupId) return;
    localStorage.setItem(PURPOSES_CACHE_PREFIX + groupId, JSON.stringify(contributionPurposes));
  }, [groupId, contributionPurposes]);

  useEffect(() => {
    if (!groupId) return;
    localStorage.setItem(`treasurer.reportHeaderMessage.${groupId}`, reportHeaderMessage || "");
  }, [groupId, reportHeaderMessage]);

  useEffect(() => {
    if (!groupId) return;
    if ((reportHeaderMessage || "").trim().length > 0) return;

    // Derive a sensible default header from the active purpose (if any),
    // falling back to the stored group info.
    let header = "";
    let purposeId = null;
    if (purposeFilterId && purposeFilterId !== "none") {
      const parsed = Number(purposeFilterId);
      if (Number.isFinite(parsed)) purposeId = parsed;
    }
    let purposeObj = null;
    if (purposeId != null) {
      purposeObj =
        contributionPurposes.find((p) => String(p.id) === String(purposeId)) || null;
    }

    const currentGroup = groups.find((g) => String(g.id) === String(groupId));
    const purposeName =
      purposeObj?.name ||
      (currentGroup?.purpose ? String(currentGroup.purpose) : "") ||
      (currentGroup?.name ? String(currentGroup.name) : "");
    const treasurerNum = (purposeObj?.treasurerNumber ?? "").trim();

    if (purposeName) {
      header = treasurerNum ? `${purposeName} – Treasurer: ${treasurerNum}` : purposeName;
    }

    if (header) {
      setReportHeaderMessage(header);
    }
  }, [groupId, groups, purposeFilterId, contributionPurposes, reportHeaderMessage]);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      return;
    }
    let isMounted = true;
    const loadRemote = async () => {
      try {
        const [groupData, membersData, transactionsData, expensesData] = await Promise.all([
          apiRequest(`/groups/${groupId}`),
          apiRequest(`/groups/${groupId}/members`),
          apiRequest(`/groups/${groupId}/transactions`),
          apiRequest(`/groups/${groupId}/expenses`).catch(() => ({ expenses: [] }))
        ]);
        if (!isMounted) {
          return;
        }
        if (groupData?.group) {
          setGroups((prev) => {
            const next = prev.map((item) =>
              String(item.id) === String(groupId)
                ? {
                    ...item,
                    name: groupData.group.name,
                    purpose: groupData.group.purpose,
                    frequency: groupData.group.frequency || "monthly",
                    createdAt: groupData.group.createdAt || item.createdAt
                  }
                : item
            );
            if (next.some((item) => String(item.id) === String(groupId))) {
              return next;
            }
            return [
              ...next,
              {
                id: String(groupData.group.id),
                name: groupData.group.name,
                purpose: groupData.group.purpose,
                frequency: groupData.group.frequency || "monthly",
                createdAt: groupData.group.createdAt || new Date().toISOString(),
                members: []
              }
            ];
          });
        }
        if (membersData?.members) {
          setGroups((prev) =>
            prev.map((item) => {
              if (String(item.id) !== String(groupId)) {
                return item;
              }
              return {
                ...item,
                members: membersData.members.map((member) => ({
                  id: member.id,
                  name: member.name,
                  phone: member.phone || "",
                  info: member.info || ""
                }))
              };
            })
          );
        }
        if (transactionsData?.transactions) {
          const normalized = transactionsData.transactions.map((transaction) => ({
            id: String(transaction.id),
            groupId: String(groupId),
            type: transaction.type,
            message: transaction.message || "",
            sender: transaction.sender || "",
            amount: Number(transaction.amount || 0),
            date: transaction.transactionDate || "",
            purposeId: transaction.purposeId != null ? transaction.purposeId : null,
            purpose: transaction.purpose || null,
            createdAt: transaction.createdAt || new Date().toISOString()
          }));
          setTransactions((prev) => {
            const other = prev.filter((item) => String(item.groupId) !== String(groupId));
            return [...normalized, ...other];
          });
        }
        if (expensesData?.expenses) {
          const normalized = expensesData.expenses.map((e) => ({
            id: String(e.id),
            groupId: String(groupId),
            categoryId: e.categoryId,
            category: e.category,
            categoryName: e.category?.name,
            amount: Number(e.amount || 0),
            expenseDate: e.expenseDate || "",
            note: e.note || "",
            createdAt: e.createdAt
          }));
          setExpenses((prev) => {
            const other = prev.filter((item) => String(item.groupId) !== String(groupId));
            return [...normalized, ...other];
          });
        }
      } catch (error) {
        // keep local data
      }
    };
    loadRemote();
    return () => {
      isMounted = false;
    };
  }, [groupId]);

  const group = useMemo(
    () => groups.find((item) => String(item.id) === String(groupId)),
    [groups, groupId]
  );

  const groupMembers = useMemo(() => {
    return Array.isArray(group?.members) ? group.members : [];
  }, [group]);

  const groupExpenses = useMemo(() => {
    return expenses
      .filter((e) => String(e.groupId) === String(groupId))
      .sort((a, b) => {
        const dA = a.expenseDate || "";
        const dB = b.expenseDate || "";
        if (dA !== dB) {
          return dB.localeCompare(dA);
        }
        return (b.createdAt || "").localeCompare(a.createdAt || "");
      });
  }, [expenses, groupId]);

  const memberPhoneLookup = useMemo(() => {
    const map = new Map();
    groupMembers.forEach((member) => {
      const normalized = normalizePhone(member.phone || "");
      if (normalized) {
        map.set(normalized, member);
      }
    });
    return map;
  }, [groupMembers]);

  const memberNameLookup = useMemo(() => {
    const map = new Map();
    groupMembers.forEach((member) => {
      if (member.name) {
        map.set(member.name.toLowerCase(), member);
      }
    });
    return map;
  }, [groupMembers]);

  const parsedTransactions = useMemo(() => {
    const mapped = transactions
      .filter((item) => String(item.groupId) === String(groupId))
      .map((item) => {
        if (item.type === "cash") {
          const senderName = item.sender || "";
          const matchedMember = senderName ? memberNameLookup.get(senderName.toLowerCase()) : null;
          const resolvedPhone = normalizePhone(item.phone || matchedMember?.phone || "");
          return {
            ...item,
            code: "CASH",
            amount: Number(item.amount) || 0,
            sender: senderName,
            phone: resolvedPhone,
            timestamp: item.date || "",
            raw: item.details || "Cash contribution",
            isValid: true
          };
        }
        const parsed = parseMpesaMessage(item.message);
        const storedSender =
          item.sender != null && String(item.sender).trim() !== "" ? String(item.sender).trim() : "";
        // Prefer ledger/backend name over SMS parse so inline edits persist (SMS text is unchanged).
        const senderName = storedSender || parsed.sender || "";
        // Phones only from stored row or SMS text. Do not infer from roster name match — that would
        // change the aggregate key after rename (e.g. LOOP B2C → a member) and leave an extra row.
        const resolvedPhone = normalizePhone(item.phone || parsed.phone || "");
        const normalizedAmount = Number(item.amount);
        const persistedCode = item.code != null && String(item.code).trim() !== "" ? String(item.code).trim() : "";
        return {
          ...item,
          ...parsed,
          // For edited amounts we want backend `transaction.amount` to be the source of truth.
          // `parseMpesaMessage` may extract a different amount from the message text.
          amount: Number.isFinite(normalizedAmount) ? normalizedAmount : (parsed.amount ?? 0),
          sender: senderName,
          phone: resolvedPhone,
          code: persistedCode || parsed.code
        };
      });
    return dedupeMpesaRowsByConfirmationCode(mapped);
  }, [transactions, groupId, memberNameLookup]);

  const ledgerCollectionsDedupedSorted = useMemo(
    () => sortContributionsByCreatedAsc(parsedTransactions),
    [parsedTransactions]
  );

  const contributorNames = useMemo(() => {
    return new Set(
      parsedTransactions
        .map((transaction) => transaction.sender)
        .filter(Boolean)
        .map((name) => name.toLowerCase())
    );
  }, [parsedTransactions]);

  const aggregatedTransactions = useMemo(() => {
    const map = new Map();
    parsedTransactions.forEach((transaction) => {
      const normalizedPhone = normalizePhone(transaction.phone || "");
      const key = normalizedPhone || `tx-${transaction.id}`;
      const amount = Number(transaction.amount) || 0;
      const codeValue = transaction.code || (transaction.type === "cash" ? "CASH" : "");
      const rawValue = transaction.raw || "";
      const timestamp = transaction.timestamp || "";
      const txDate = parseDateValue(timestamp);
      const memberMatch = normalizedPhone ? memberPhoneLookup.get(normalizedPhone) : null;

      if (!map.has(key)) {
        map.set(key, {
          ...transaction,
          phone: normalizedPhone,
          sender: memberMatch?.name || transaction.sender || "",
          amount,
          // Preserve the original amount for the first contribution in this group.
          // Used to avoid overwriting the amount when editing purpose while in grouped mode.
          firstAmount: amount,
          contributionCount: 1,
          codes: codeValue ? [codeValue] : [],
          rawDetails: rawValue ? [rawValue] : [],
          sortDate: txDate
        });
        return;
      }

      const current = map.get(key);
      current.amount += amount;
      current.contributionCount = (current.contributionCount || 1) + 1;
      if (codeValue && !current.codes.includes(codeValue)) {
        current.codes.push(codeValue);
      }
      if (rawValue) {
        current.rawDetails.push(rawValue);
      }
      if (txDate && (!current.sortDate || txDate > current.sortDate)) {
        current.timestamp = timestamp;
        current.sortDate = txDate;
      }
    });

    return Array.from(map.values()).map((item) => ({
      ...item,
      code: item.codes.join(", "),
      raw: item.rawDetails.join("\n"),
      contributionCount: item.contributionCount ?? 1
    }));
  }, [parsedTransactions, memberPhoneLookup]);

  const contributedMembers = useMemo(() => {
    return groupMembers.filter((member) => contributorNames.has(member.name.toLowerCase()));
  }, [groupMembers, contributorNames]);

  const membersPieSeries = useMemo(() => {
    if (groupMembers.length === 0) {
      return [];
    }
    const contributedCount = contributedMembers.length;
    const remaining = Math.max(groupMembers.length - contributedCount, 0);
    // Order: [Not yet, Contributed] so Chartist colors them red, green
    return [remaining, contributedCount];
  }, [groupMembers.length, contributedMembers.length]);

  const contributionBarData = useMemo(() => {
    if (!group || !["weekly", "monthly"].includes(group.frequency)) {
      return null;
    }
    const bucketMap = new Map();
    ledgerCollectionsDedupedSorted.forEach((transaction) => {
      const date = parseDateValue(transaction.timestamp);
      if (!date) {
        return;
      }
      let key = "";
      let label = "";
      let sortDate = new Date(date);
      if (group.frequency === "weekly") {
        const week = getWeekKey(date);
        key = week.key;
        label = `W${week.weekNumber} ${week.year}`;
        sortDate = new Date(Date.UTC(week.year, 0, 1 + (week.weekNumber - 1) * 7));
      } else {
        const year = date.getFullYear();
        const month = date.getMonth();
        key = `${year}-${String(month + 1).padStart(2, "0")}`;
        label = date.toLocaleDateString("en-KE", { month: "short", year: "numeric" });
        sortDate = new Date(year, month, 1);
      }
      const current = bucketMap.get(key) || { label, total: 0, sortDate };
      current.total += transaction.amount || 0;
      bucketMap.set(key, current);
    });
    const buckets = Array.from(bucketMap.values()).sort((a, b) => a.sortDate - b.sortDate);
    if (buckets.length === 0) {
      return null;
    }
    return {
      labels: buckets.map((bucket) => bucket.label),
      series: [buckets.map((bucket) => bucket.total)]
    };
  }, [group, ledgerCollectionsDedupedSorted]);

  const totalAmount = useMemo(
    () =>
      ledgerCollectionsDedupedSorted.reduce((sum, transaction) => sum + (Number(transaction.amount) || 0), 0),
    [ledgerCollectionsDedupedSorted]
  );

  const totalExpensesAmount = useMemo(
    () => groupExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0),
    [groupExpenses]
  );

  const balanceAmount = useMemo(() => totalAmount - totalExpensesAmount, [totalAmount, totalExpensesAmount]);

  const purposeLookup = useMemo(() => {
    const map = new Map();
    contributionPurposes.forEach((p) => {
      map.set(p.id, p);
      map.set(Number(p.id), p);
    });
    return map;
  }, [contributionPurposes]);

  const numericPurposes = useMemo(() => {
    return contributionPurposes.filter((p) => {
      const raw = String(p.id ?? "");
      return /^\d+$/.test(raw);
    });
  }, [contributionPurposes]);

  const filteredTransactions = useMemo(() => {
    let list = aggregatedTransactions;
    if (purposeFilterId === "none") {
      list = list.filter((t) => t.purposeId == null);
    } else if (purposeFilterId) {
      const id = Number(purposeFilterId);
      list = list.filter((t) => t.purposeId != null && (Number(t.purposeId) === id || t.purposeId === id));
    }
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return list;
    }
    return list.filter((transaction) => {
      const purposeName = transaction.purposeId != null
        ? (purposeLookup.get(transaction.purposeId) || purposeLookup.get(String(transaction.purposeId)))?.name || ""
        : "";
      const haystack = [
        transaction.code,
        transaction.sender,
        transaction.timestamp,
        transaction.raw,
        transaction.phone,
        purposeName,
        String(transaction.amount ?? "")
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [aggregatedTransactions, searchTerm, purposeFilterId, purposeLookup]);

  const sortKeyName = (t) => (t.sender || "").trim().toLowerCase();

  const sortedFlatTransactions = useMemo(() => {
    return [...parsedTransactions].sort((a, b) => {
      const dateA = parseDateValue(a.timestamp || a.date);
      const dateB = parseDateValue(b.timestamp || b.date);
      return (dateB?.getTime() || 0) - (dateA?.getTime() || 0);
    });
  }, [parsedTransactions]);

  const filteredFlatTransactions = useMemo(() => {
    let list = sortedFlatTransactions;
    if (purposeFilterId === "none") {
      list = list.filter((t) => t.purposeId == null);
    } else if (purposeFilterId) {
      const id = Number(purposeFilterId);
      list = list.filter((t) => t.purposeId != null && (Number(t.purposeId) === id || t.purposeId === id));
    }
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return list;
    }
    return list.filter((transaction) => {
      const purposeName = transaction.purposeId != null
        ? (purposeLookup.get(transaction.purposeId) || purposeLookup.get(String(transaction.purposeId)))?.name || ""
        : "";
      const haystack = [
        transaction.code,
        transaction.sender,
        transaction.timestamp,
        transaction.raw,
        transaction.phone,
        purposeName,
        String(transaction.amount ?? "")
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [sortedFlatTransactions, searchTerm, purposeFilterId, purposeLookup]);

  const listForTable = useMemo(() => {
    const base = displayMode === "all" ? filteredFlatTransactions : filteredTransactions;
    const copy = [...base];
    if (contributionSort === "name_asc") {
      copy.sort((a, b) => sortKeyName(a).localeCompare(sortKeyName(b)));
    } else if (contributionSort === "name_desc") {
      copy.sort((a, b) => sortKeyName(b).localeCompare(sortKeyName(a)));
    } else {
      copy.sort((a, b) => {
        const dateA = parseDateValue(a.timestamp || a.date);
        const dateB = parseDateValue(b.timestamp || b.date);
        return (dateB?.getTime() || 0) - (dateA?.getTime() || 0);
      });
    }
    return copy;
  }, [
    displayMode,
    filteredFlatTransactions,
    filteredTransactions,
    contributionSort
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, purposeFilterId, pageSize, groupId, displayMode, contributionSort]);
  const totalPages = Math.max(1, Math.ceil(listForTable.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedTransactions = listForTable.slice(startIndex, startIndex + pageSize);

  const handleUpdatePurpose = (transaction, newPurposeId) => {
    const raw = newPurposeId ?? "";
    const purposeIdNum = raw === "" ? null : Number(raw);
    if (purposeIdNum !== null && Number.isNaN(purposeIdNum)) {
      // Likely a temporary local id like "purp-123" which the backend can't resolve.
      setUpdatingPurposeId(null);
      return;
    }
    const amountValue =
      displayMode === "grouped" && transaction.firstAmount != null
        ? transaction.firstAmount
        : transaction.amount;
    const amountNum = Number(amountValue);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setUpdatingPurposeId(null);
      return;
    }
    setUpdatingPurposeId(transaction.id);
    apiRequest(`/transactions/${transaction.id}`, {
      method: "PUT",
      body: JSON.stringify({
        type: transaction.type,
        code: resolveMpesaCodeForPutBody(transaction),
        sender: transaction.sender ?? null,
        amount: amountNum,
        message: transaction.message ?? null,
        transactionDate: transaction.timestamp || transaction.date || null,
        purposeId: purposeIdNum
      })
    })
      .then(() => {
        setTransactions((prev) =>
          prev.map((t) =>
            String(t.id) === String(transaction.id) ? { ...t, purposeId: purposeIdNum } : t
          )
        );
      })
      .catch(() => {})
      .finally(() => setUpdatingPurposeId(null));
  };

  const canEditAmountInline = (transaction) =>
    displayMode === "all" ||
    (displayMode === "grouped" && (transaction.contributionCount ?? 1) === 1);

  /** Like purpose, reference can be edited in grouped mode (updates the first row in that phone group). */
  const canEditCodeInline = (transaction) => transaction.type !== "cash";

  const handleOpenIndividualAmounts = (transaction) => {
    setDisplayMode("all");
    setSearchTerm((transaction.sender || "").trim());
    setCurrentPage(1);
    requestAnimationFrame(() => {
      const tableEl = document.getElementById("contributions-table");
      if (tableEl) {
        tableEl.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  };

  const handleStartEditAmount = (transaction) => {
    if (!canEditAmountInline(transaction)) return;
    handleCancelEditSender();
    handleCancelEditCode();
    setAmountError("");
    setEditingAmountId(transaction.id);
    setAmountEditValue(String(transaction.amount ?? ""));
  };

  const handleCancelEditAmount = () => {
    setAmountError("");
    setEditingAmountId(null);
    setAmountEditValue("");
  };

  const handleCancelEditSender = () => {
    setSenderEditError("");
    setEditingSenderId(null);
    setSenderEditValue("");
  };

  const handleCancelEditCode = () => {
    setCodeEditError("");
    setEditingCodeId(null);
    setCodeEditValue("");
  };

  const handleStartEditSender = (transaction) => {
    if (!canEditAmountInline(transaction)) return;
    handleCancelEditAmount();
    handleCancelEditCode();
    setSenderEditError("");
    setEditingSenderId(transaction.id);
    setSenderEditValue(String(transaction.sender ?? "").trim());
  };

  const handleStartEditCode = (transaction) => {
    if (transaction.type === "cash") return;
    handleCancelEditAmount();
    handleCancelEditSender();
    setCodeEditError("");
    setEditingCodeId(transaction.id);
    setCodeEditValue(String(transaction.code ?? "").trim());
  };

  const handleSaveEditSender = async (transaction) => {
    if (!canEditAmountInline(transaction)) return;
    const nextName = senderEditValue.trim();
    if (!nextName) {
      setSenderEditError("Enter a contributor name.");
      return;
    }

    setSenderEditError("");
    setUpdatingSenderId(transaction.id);

    const purposeIdNum =
      transaction.purposeId != null && transaction.purposeId !== ""
        ? Number(transaction.purposeId)
        : null;
    const amountNum = Number(transaction.amount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setSenderEditError("Invalid amount on this row; refresh the page.");
      setUpdatingSenderId(null);
      return;
    }

    try {
      const data = await apiRequest(`/transactions/${transaction.id}`, {
        method: "PUT",
        body: JSON.stringify({
          type: transaction.type,
          code: resolveMpesaCodeForPutBody(transaction),
          sender: nextName,
          amount: amountNum,
          message: transaction.message ?? null,
          transactionDate: transaction.timestamp || transaction.date || null,
          purposeId: purposeIdNum
        })
      });

      const updated = data?.transaction;
      if (updated) {
        setTransactions((prev) =>
          prev.map((t) =>
            String(t.id) === String(transaction.id)
              ? {
                  ...t,
                  sender: updated.sender != null ? String(updated.sender) : nextName,
                  ...(updated.code != null && String(updated.code).trim() !== ""
                    ? { code: String(updated.code).trim() }
                    : {})
                }
              : t
          )
        );
      }

      handleCancelEditSender();
    } catch (err) {
      setSenderEditError(err?.message || "Failed to update name.");
    } finally {
      setUpdatingSenderId(null);
    }
  };

  const handleSaveEditCode = async (transaction) => {
    if (transaction.type === "cash") return;
    const nextCode = codeEditValue.trim();
    setCodeEditError("");
    setUpdatingCodeId(transaction.id);

    const purposeIdNum =
      transaction.purposeId != null && transaction.purposeId !== ""
        ? Number(transaction.purposeId)
        : null;
    const amountNum = Number(transaction.amount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setCodeEditError("Invalid amount on this row; refresh the page.");
      setUpdatingCodeId(null);
      return;
    }

    try {
      const data = await apiRequest(`/transactions/${transaction.id}`, {
        method: "PUT",
        body: JSON.stringify({
          type: transaction.type,
          code: nextCode || null,
          sender: transaction.sender || null,
          amount: amountNum,
          message: transaction.message || null,
          transactionDate: transaction.timestamp || transaction.date || null,
          purposeId: purposeIdNum
        })
      });

      const updated = data?.transaction;
      if (updated) {
        setTransactions((prev) =>
          prev.map((t) =>
            String(t.id) === String(transaction.id)
              ? {
                  ...t,
                  code: updated.code != null ? String(updated.code) : ""
                }
              : t
          )
        );
      }

      handleCancelEditCode();
    } catch (err) {
      setCodeEditError(err?.message || "Failed to update reference.");
    } finally {
      setUpdatingCodeId(null);
    }
  };

  const isPersistedTransactionId = (id) => /^\d+$/.test(String(id));

  const requestDeleteContribution = (transaction) => {
    if (!canEditAmountInline(transaction)) return;
    setDeleteError("");
    setTransactionPendingDelete(transaction);
    setShowDeleteModal(true);
  };

  const handleCancelDeleteContribution = () => {
    if (deletingId != null) return;
    setShowDeleteModal(false);
    setTransactionPendingDelete(null);
    setDeleteError("");
  };

  const handleConfirmDeleteContribution = async () => {
    const tx = transactionPendingDelete;
    if (!tx) return;
    const idStr = String(tx.id);
    setDeleteError("");
    setDeletingId(tx.id);
    try {
      if (isPersistedTransactionId(idStr)) {
        await apiRequest(`/transactions/${idStr}`, { method: "DELETE" });
      }
      setTransactions((prev) => prev.filter((t) => String(t.id) !== idStr));
      if (String(editingAmountId) === idStr) {
        handleCancelEditAmount();
      }
      if (String(editingSenderId) === idStr) {
        handleCancelEditSender();
      }
      if (String(editingCodeId) === idStr) {
        handleCancelEditCode();
      }
      setShowDeleteModal(false);
      setTransactionPendingDelete(null);
      showToast("success", "Contribution deleted.");
    } catch (err) {
      setDeleteError(err?.message || "Could not delete this contribution.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleSaveEditAmount = async (transaction) => {
    if (!canEditAmountInline(transaction)) return;
    const nextAmount = Number(amountEditValue);
    if (!Number.isFinite(nextAmount) || nextAmount <= 0) {
      setAmountError("Enter a valid amount greater than 0.");
      return;
    }

    setAmountError("");
    setUpdatingAmountId(transaction.id);

    const purposeIdNum =
      transaction.purposeId != null && transaction.purposeId !== ""
        ? Number(transaction.purposeId)
        : null;

    try {
      const data = await apiRequest(`/transactions/${transaction.id}`, {
        method: "PUT",
        body: JSON.stringify({
          type: transaction.type,
          code: resolveMpesaCodeForPutBody(transaction),
          sender: transaction.sender || null,
          amount: nextAmount,
          message: transaction.message || null,
          transactionDate: transaction.timestamp || transaction.date || null,
          purposeId: purposeIdNum
        })
      });

      const updated = data?.transaction;
      if (updated) {
        setTransactions((prev) =>
          prev.map((t) =>
            String(t.id) === String(transaction.id)
              ? {
                  ...t,
                  amount: Number(updated.amount),
                  purposeId: updated.purposeId ?? t.purposeId,
                  message: updated.message ?? t.message,
                  ...(updated.code != null && String(updated.code).trim() !== ""
                    ? { code: String(updated.code).trim() }
                    : {})
                }
              : t
          )
        );
      }

      handleCancelEditAmount();
    } catch (err) {
      setAmountError(err?.message || "Failed to update amount.");
    } finally {
      setUpdatingAmountId(null);
    }
  };

  const getPurposeName = (purposeId) => {
    if (purposeId == null) return "—";
    const p = purposeLookup.get(Number(purposeId)) || purposeLookup.get(String(purposeId));
    return p?.name || "—";
  };

  const handleAddPurpose = (e) => {
    e.preventDefault();
    const name = (purposeForm?.name ?? "").trim();
    if (!name) return;
    setPurposeError("");
    const treasurerNumber = (purposeForm?.treasurerNumber ?? "").trim() || null;
    const newPurpose = {
      id: `purp-${Date.now()}`,
      name,
      description: (purposeForm?.description ?? "").trim() || "",
      treasurerNumber: treasurerNumber || ""
    };
    setContributionPurposes((prev) => [...prev, newPurpose]);
    setPurposeForm({ name: "", description: "", treasurerNumber: "" });
    const tempId = newPurpose.id;
    const token = getAuthToken();
    if (token && navigator.onLine) {
      apiRequest(`/groups/${groupId}/contribution-purposes`, {
        method: "POST",
        body: JSON.stringify({ name, description: newPurpose.description || undefined, treasurerNumber: treasurerNumber || undefined })
      })
        .then((data) => {
          if (data?.purpose?.id) {
            setContributionPurposes((prev) =>
              prev.map((p) =>
                p.id === tempId
                  ? { ...p, id: String(data.purpose.id), treasurerNumber: data.purpose.treasurerNumber || "" }
                  : p
              )
            );
          }
        })
        .catch((err) => {
          setContributionPurposes((prev) => prev.filter((p) => p.id !== tempId));
          setPurposeError(err?.message || "Failed to add purpose");
        });
    }
  };

  const handleStartEditPurpose = (purpose) => {
    setEditingPurposeId(purpose != null ? String(purpose.id) : null);
    setEditingPurposeValues({
      name: purpose.name || "",
      description: purpose.description || "",
      treasurerNumber: purpose.treasurerNumber || ""
    });
    setPurposeError("");
  };

  const handleCancelEditPurpose = () => {
    setEditingPurposeId(null);
    setEditingPurposeValues({ name: "", description: "", treasurerNumber: "" });
  };

  const handleSaveEditPurpose = (purpose) => {
    const name = (editingPurposeValues?.name ?? "").trim();
    if (!name) return;
    setPurposeError("");
    const desc = (editingPurposeValues?.description ?? "").trim();
    const treasurerNumber = (editingPurposeValues?.treasurerNumber ?? "").trim() || "";
    setContributionPurposes((prev) =>
      prev.map((p) => (p.id === purpose.id ? { ...p, name, description: desc || "", treasurerNumber } : p))
    );
    setEditingPurposeId(null);
    setEditingPurposeValues({ name: "", description: "", treasurerNumber: "" });
    if (!String(purpose.id).startsWith("purp-") && navigator.onLine) {
      apiRequest(`/contribution-purposes/${purpose.id}`, {
        method: "PUT",
        body: JSON.stringify({ name, description: desc || undefined, treasurerNumber: treasurerNumber || undefined })
      }).catch((err) => setPurposeError(err?.message || "Failed to update"));
    }
  };

  const handleDeletePurpose = async (purpose) => {
    setPurposeError("");
    setContributionPurposes((prev) => prev.filter((p) => p.id !== purpose.id));
    if (!String(purpose.id).startsWith("purp-") && navigator.onLine) {
      try {
        await apiRequest(`/contribution-purposes/${purpose.id}`, { method: "DELETE" });
      } catch (err) {
        setContributionPurposes((prev) => [...prev, purpose]);
        setPurposeError(err?.message || "Cannot delete (may be in use by contributions)");
      }
    }
  };

  const contributionsForReport = useMemo(() => {
    let list = parsedTransactions;
    if (purposeFilterId === "none") {
      list = list.filter((t) => t.purposeId == null);
    } else if (purposeFilterId) {
      const id = Number(purposeFilterId);
      list = list.filter((t) => t.purposeId != null && Number(t.purposeId) === id);
    }
    return sortContributionsByCreatedAsc(list);
  }, [parsedTransactions, purposeFilterId]);

  const contributionReportText = useMemo(() => {
    return buildContributionReport(
      group,
      contributionsForReport,
      groupExpenses,
      purposeLookup,
      reportHeaderMessage
    );
  }, [group, contributionsForReport, groupExpenses, purposeLookup, reportHeaderMessage]);

  const handleClosePreview = () => {
    setShowPreview(false);
  };

  const handleCopyReport = async () => {
    try {
      await navigator.clipboard.writeText(contributionReportText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setCopied(false);
    }
  };

  return (
    <>
      <ConfirmationToast
        show={toast.show}
        variant={toast.variant}
        message={toast.message}
        onClose={closeToast}
        successTitle="Done"
      />

      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <Breadcrumb className="d-none d-md-inline-block" listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}>
            <Breadcrumb.Item linkProps={{ as: Link, to: Routes.Presentation.path }}>
              <FontAwesomeIcon icon={faHome} />
            </Breadcrumb.Item>
            <Breadcrumb.Item linkProps={{ as: Link, to: Routes.Groups.path }}>
              Groups
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Ledger</Breadcrumb.Item>
          </Breadcrumb>
          <h4>{group ? group.name : "Group ledger"}</h4>
          <p className="mb-0">{group ? group.purpose : "Money in, money out, and balance for this group."}</p>
        </div>
        <div className="btn-toolbar mb-2 mb-md-0">
          <Button as={Link} variant="outline-primary" size="sm" to={Routes.Groups.path}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to groups
          </Button>
        </div>
      </div>

      <Row className="mb-4">
        <Col md={3} className="mb-3 mb-md-0">
          <Link to={generatePath(Routes.GroupRecordIncome.path, { groupId })} className="text-decoration-none text-body d-block h-100" style={{ cursor: "pointer" }}>
            <Card border="light" className="shadow-sm h-100">
              <Card.Body className="text-center">
                <div className="text-muted small mb-1">💰 Collections</div>
                <div className="h4 mb-0">{formatReportNumber(totalAmount)}</div>
              </Card.Body>
            </Card>
          </Link>
        </Col>
        <Col md={3} className="mb-3 mb-md-0">
          <Link to={generatePath(Routes.GroupRecordExpenses.path, { groupId })} className="text-decoration-none text-body d-block h-100" style={{ cursor: "pointer" }}>
            <Card border="light" className="shadow-sm h-100">
              <Card.Body className="text-center">
                <div className="text-muted small mb-1">💸 Expenses</div>
                <div className="h4 mb-0">{formatReportNumber(totalExpensesAmount)}</div>
              </Card.Body>
            </Card>
          </Link>
        </Col>
        <Col md={3} className="mb-3 mb-md-0">
          <Card
            border="light"
            className="shadow-sm h-100"
            style={{ cursor: "pointer" }}
            onClick={() => setShowPreview(true)}
          >
              <Card.Body className="text-center">
                <div className="text-muted small mb-1">📊 Balance</div>
              <div className="h4 mb-0">{formatReportNumber(balanceAmount)}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Link to={generatePath(Routes.GroupMembers.path, { groupId })} className="text-decoration-none text-body d-block h-100" style={{ cursor: "pointer" }}>
            <Card border="light" className="shadow-sm h-100">
              <Card.Body className="text-center">
                <div className="text-muted small mb-1">👥 Members</div>
                <div className="h4 mb-0">{groupMembers.length}</div>
              </Card.Body>
            </Card>
          </Link>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col xs={12} className="d-flex justify-content-between align-items-center">
          <div className="text-muted small">
            Record new income and track member payments.
          </div>
          <Button
            as={Link}
            to={generatePath(Routes.GroupRecordIncome.path, { groupId })}
            variant="primary"
            size="sm"
          >
            + Add contribution
          </Button>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col xs={12} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h5 className="mb-0">
                Contribution purposes
                <span className="text-muted fw-normal ms-2">({contributionPurposes.length})</span>
              </h5>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowPurposesCard((v) => !v)}
              >
                {showPurposesCard ? "Hide" : "Edit purposes"}
              </Button>
            </Card.Header>
            {showPurposesCard ? (
              <Card.Body className="pt-0">
                {purposeError ? <div className="text-danger small mb-2">{purposeError}</div> : null}
                <Form onSubmit={handleAddPurpose} className="mb-3">
                  <Row className="g-2 align-items-end">
                    <Col md={3}>
                      <Form.Group className="mb-0">
                        <Form.Label className="small">Name</Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="e.g. Burial, Wedding"
                          value={purposeForm?.name ?? ""}
                          onChange={(e) => {
                          const value = e.target != null ? e.target.value : "";
                          setPurposeForm((prev) => ({ ...prev, name: value }));
                        }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-0">
                        <Form.Label className="small">Treasurer number (optional)</Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="e.g. 0712345678"
                          value={purposeForm?.treasurerNumber ?? ""}
                          onChange={(e) => {
                          const value = e.target != null ? e.target.value : "";
                          setPurposeForm((prev) => ({ ...prev, treasurerNumber: value }));
                        }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-0">
                        <Form.Label className="small">Description (optional)</Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="Optional"
                          value={purposeForm?.description ?? ""}
                          onChange={(e) => {
                          const value = e.target != null ? e.target.value : "";
                          setPurposeForm((prev) => ({ ...prev, description: value }));
                        }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Button type="submit" size="sm" variant="primary" disabled={!(purposeForm?.name ?? "").trim()}>
                        Add purpose
                      </Button>
                    </Col>
                  </Row>
                </Form>
                {contributionPurposes.length === 0 ? (
                  <p className="text-muted small mb-0">No purposes yet. Add one above; they appear in the purpose dropdown when recording income and in the contributions table.</p>
                ) : (
                  <Table responsive size="sm" className="table-centered mb-0">
                    <thead className="thead-light">
                      <tr>
                        <th className="border-0">Name</th>
                        <th className="border-0">Treasurer number</th>
                        <th className="border-0">Description</th>
                        <th className="border-0 text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contributionPurposes.filter(Boolean).map((purpose) => {
                        const isEditing = editingPurposeId != null && String(editingPurposeId) === String(purpose.id);
                        return (
                          <tr key={purpose.id}>
                            <td>
                              {isEditing ? (
                                <Form.Control
                                  size="sm"
                                  value={editingPurposeValues?.name ?? ""}
                                  onChange={(e) => {
                                  const value = e.target != null ? e.target.value : "";
                                  setEditingPurposeValues((prev) => ({ ...prev, name: value }));
                                }}
                                />
                              ) : (
                                purpose.name
                              )}
                            </td>
                            <td>
                              {isEditing ? (
                                <Form.Control
                                  size="sm"
                                  placeholder="Optional"
                                  value={editingPurposeValues?.treasurerNumber ?? ""}
                                  onChange={(e) => {
                                  const value = e.target != null ? e.target.value : "";
                                  setEditingPurposeValues((prev) => ({ ...prev, treasurerNumber: value }));
                                }}
                                />
                              ) : (
                                purpose.treasurerNumber || "–"
                              )}
                            </td>
                            <td>
                              {isEditing ? (
                                <Form.Control
                                  size="sm"
                                  value={editingPurposeValues?.description ?? ""}
                                  onChange={(e) => {
                                  const value = e.target != null ? e.target.value : "";
                                  setEditingPurposeValues((prev) => ({ ...prev, description: value }));
                                }}
                                />
                              ) : (
                                purpose.description || "–"
                              )}
                            </td>
                            <td className="text-end">
                              {isEditing ? (
                                <>
                                  <Button size="sm" variant="outline-primary" className="me-1" onClick={() => handleSaveEditPurpose(purpose)} disabled={!(editingPurposeValues?.name ?? "").trim()}>
                                    Save
                                  </Button>
                                  <Button size="sm" variant="outline-secondary" onClick={handleCancelEditPurpose}>
                                    Cancel
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => handleStartEditPurpose(purpose)}>
                                    Edit
                                  </Button>
                                  <Button size="sm" variant="outline-danger" onClick={() => handleDeletePurpose(purpose)}>
                                    Delete
                                  </Button>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            ) : null}
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col xs={12}>
          <Card border="light" className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h5 className="mb-0">Contributions</h5>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">{formatAmount(totalAmount)} total</span>
              </div>
            </Card.Header>
            <Card.Body className="pt-0">
              {(() => {
                const uncategorizedOnPage = paginatedTransactions.filter((t) => t.purposeId == null || t.purposeId === "").length;
                if (!uncategorizedOnPage) return null;
                return (
                  <Alert variant="warning" className="d-flex flex-wrap align-items-center justify-content-between mb-3 py-2">
                    <span className="small mb-1 mb-md-0">
                      ⚠️ {uncategorizedOnPage} contribution{uncategorizedOnPage === 1 ? "" : "s"} need categorization
                    </span>
                    <Button
                      variant="outline-warning"
                      size="sm"
                      onClick={() => {
                        setPurposeFilterId("");
                        const el = document.getElementById("contributions-table");
                        if (el && typeof el.scrollIntoView === "function") {
                          el.scrollIntoView({ behavior: "smooth", block: "start" });
                        }
                      }}
                    >
                      Review now
                    </Button>
                  </Alert>
                );
              })()}
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 py-3">
                <div className="d-flex flex-wrap gap-2 align-items-center flex-grow-1">
                  <Form.Control
                    type="text"
                    placeholder="Search by name, amount or date"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="flex-grow-1"
                    style={{ minWidth: "160px" }}
                  />
                  <Form.Select
                    size="sm"
                    value={purposeFilterId}
                    onChange={(e) => setPurposeFilterId(e?.target?.value ?? "")}
                    style={{ width: "auto", minWidth: "140px" }}
                    title="Filter by purpose"
                  >
                    <option value="">All purposes</option>
                    <option value="none">Uncategorized</option>
                    {numericPurposes.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Form.Select
                    size="sm"
                    value={displayMode}
                    onChange={(e) => setDisplayMode(e?.target?.value ?? "grouped")}
                    style={{ width: "auto" }}
                    aria-label="Contribution list display mode"
                  >
                    <option value="grouped">Grouped by sender</option>
                    <option value="all">All contributions (edit names and amounts)</option>
                  </Form.Select>
                  <Form.Select
                    size="sm"
                    value={contributionSort}
                    onChange={(e) => setContributionSort(e?.target?.value ?? "date_desc")}
                    style={{ width: "auto", minWidth: "9rem" }}
                    aria-label="Sort contributions"
                    title="Sort order"
                  >
                    <option value="date_desc">Newest first</option>
                    <option value="name_asc">Name A–Z</option>
                    <option value="name_desc">Name Z–A</option>
                  </Form.Select>
                  <small className="text-muted text-nowrap">
                    {listForTable.length} result{listForTable.length === 1 ? "" : "s"}
                  </small>
                  <Form.Select
                    value={pageSize}
                    onChange={(event) => setPageSize(Number(event.target.value))}
                  >
                    <option value={10}>10 / page</option>
                    <option value={20}>20 / page</option>
                    <option value={50}>50 / page</option>
                  </Form.Select>
                </div>
              </div>
              <Table id="contributions-table" responsive className="table-centered align-items-center mb-0">
                <thead className="thead-light">
                  <tr>
                    <th className="border-0">Name</th>
                    <th className="border-0 text-end">Amount</th>
                    <th className="border-0">Date</th>
                    <th className="border-0">Reference</th>
                    <th className="border-0">Purpose</th>
                    <th className="border-0 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTransactions.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-4 text-muted">
                        {(displayMode === "grouped" ? aggregatedTransactions : sortedFlatTransactions).length === 0
                          ? <>No contributions yet. Go to <Link to={generatePath(Routes.GroupRecordIncome.path, { groupId })}>Record income</Link> to add.</>
                          : "No matches for your search."}
                      </td>
                    </tr>
                  ) : (
                    paginatedTransactions.map((transaction) => (
                      <tr key={transaction.id}>
                        <td>
                          {canEditAmountInline(transaction) ? (
                            editingSenderId === transaction.id ? (
                              <div>
                                <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-start gap-2">
                                  <Form.Control
                                    size="sm"
                                    type="text"
                                    value={senderEditValue}
                                    onChange={(e) => setSenderEditValue(e?.target?.value ?? "")}
                                    style={{ minWidth: "10rem", maxWidth: "18rem" }}
                                    disabled={updatingSenderId === transaction.id}
                                  />
                                  <div className="d-flex flex-column flex-sm-row gap-1 flex-shrink-0">
                                    <Button
                                      size="sm"
                                      variant="outline-primary"
                                      onClick={() => handleSaveEditSender(transaction)}
                                      disabled={updatingSenderId === transaction.id}
                                    >
                                      {updatingSenderId === transaction.id ? "Saving..." : "Save"}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-secondary"
                                      onClick={handleCancelEditSender}
                                      disabled={updatingSenderId === transaction.id}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                                {senderEditError ? (
                                  <small className="text-danger d-block mt-1">{senderEditError}</small>
                                ) : null}
                                {transaction.raw ? (
                                  <OverlayTrigger
                                    trigger={["hover", "focus", "click"]}
                                    placement="top"
                                    overlay={<Tooltip>{transaction.raw}</Tooltip>}
                                  >
                                    <Button variant="link" className="p-0 text-muted text-decoration-none small mt-1">
                                      Details
                                    </Button>
                                  </OverlayTrigger>
                                ) : null}
                              </div>
                            ) : (
                              <>
                                <Button
                                  variant="link"
                                  className="p-0 text-body text-decoration-none text-start"
                                  onClick={() => handleStartEditSender(transaction)}
                                  title="Edit contributor name"
                                  disabled={
                                    updatingSenderId != null ||
                                    updatingAmountId === transaction.id ||
                                    updatingPurposeId === transaction.id ||
                                    editingCodeId === transaction.id ||
                                    updatingCodeId === transaction.id
                                  }
                                >
                                  {formatPersonName(transaction.sender) || "--"}
                                </Button>
                                {transaction.raw ? (
                                  <OverlayTrigger
                                    trigger={["hover", "focus", "click"]}
                                    placement="top"
                                    overlay={<Tooltip>{transaction.raw}</Tooltip>}
                                  >
                                    <Button variant="link" className="p-0 text-muted text-decoration-none small d-block">
                                      Details
                                    </Button>
                                  </OverlayTrigger>
                                ) : null}
                              </>
                            )
                          ) : (
                            <>
                              <div>{formatPersonName(transaction.sender) || "--"}</div>
                              {transaction.raw ? (
                                <OverlayTrigger
                                  trigger={["hover", "focus", "click"]}
                                  placement="top"
                                  overlay={<Tooltip>{transaction.raw}</Tooltip>}
                                >
                                  <Button variant="link" className="p-0 text-muted text-decoration-none small">
                                    Details
                                  </Button>
                                </OverlayTrigger>
                              ) : null}
                            </>
                          )}
                        </td>
                        <td className="text-end">
                          {!canEditAmountInline(transaction) && displayMode === "grouped" ? (
                            <Button
                              variant="link"
                              className="p-0 text-body text-decoration-none small"
                              onClick={() => handleOpenIndividualAmounts(transaction)}
                              title="Opens the full list filtered to this person so you can edit each amount"
                            >
                              {formatAmount(transaction.amount)}
                            </Button>
                          ) : editingAmountId === transaction.id ? (
                            <div className="d-flex justify-content-end align-items-start gap-2">
                              <div style={{ minWidth: 140 }}>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={amountEditValue}
                                  onChange={(e) => setAmountEditValue(e?.target?.value ?? "")}
                                  style={{ textAlign: "right" }}
                                />
                                {amountError ? (
                                  <small className="text-danger d-block mt-1">{amountError}</small>
                                ) : null}
                              </div>
                              <div className="d-flex flex-column">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => handleSaveEditAmount(transaction)}
                                  disabled={updatingAmountId === transaction.id}
                                >
                                  {updatingAmountId === transaction.id ? "Saving..." : "Save"}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  className="mt-1"
                                  onClick={handleCancelEditAmount}
                                  disabled={updatingAmountId === transaction.id}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <Button
                              variant="link"
                              className="p-0 text-body text-decoration-none small"
                              onClick={() => handleStartEditAmount(transaction)}
                              title="Edit amount"
                              disabled={
                                editingSenderId === transaction.id ||
                                updatingSenderId === transaction.id ||
                                updatingPurposeId === transaction.id ||
                                editingCodeId === transaction.id ||
                                updatingCodeId === transaction.id
                              }
                            >
                              {formatAmount(transaction.amount)}
                            </Button>
                          )}
                        </td>
                        <td>{formatTableDate(transaction.timestamp)}</td>
                        <td className="fw-bold">
                          {transaction.type === "cash" ? (
                            transaction.code || "CASH"
                          ) : canEditCodeInline(transaction) ? (
                            editingCodeId === transaction.id ? (
                              <div>
                                <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-start gap-2">
                                  <Form.Control
                                    size="sm"
                                    type="text"
                                    value={codeEditValue}
                                    onChange={(e) => setCodeEditValue(e?.target?.value ?? "")}
                                    style={{ minWidth: "6rem", maxWidth: "14rem" }}
                                    disabled={updatingCodeId === transaction.id}
                                    placeholder="M-Pesa ref"
                                  />
                                  <div className="d-flex flex-column flex-sm-row gap-1 flex-shrink-0">
                                    <Button
                                      size="sm"
                                      variant="outline-primary"
                                      onClick={() => handleSaveEditCode(transaction)}
                                      disabled={updatingCodeId === transaction.id}
                                    >
                                      {updatingCodeId === transaction.id ? "Saving..." : "Save"}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-secondary"
                                      onClick={handleCancelEditCode}
                                      disabled={updatingCodeId === transaction.id}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                                {codeEditError ? (
                                  <small className="text-danger d-block mt-1">{codeEditError}</small>
                                ) : null}
                              </div>
                            ) : (
                              <Button
                                variant="link"
                                className="p-0 text-body text-decoration-none text-start fw-bold"
                                onClick={() => handleStartEditCode(transaction)}
                                title={
                                  displayMode === "grouped" && (transaction.contributionCount ?? 1) > 1
                                    ? "Edit reference on the first contribution in this group (same as purpose)"
                                    : "Edit reference / M-Pesa code"
                                }
                                disabled={
                                  updatingCodeId != null ||
                                  editingAmountId === transaction.id ||
                                  editingSenderId === transaction.id ||
                                  updatingAmountId === transaction.id ||
                                  updatingSenderId === transaction.id ||
                                  updatingPurposeId === transaction.id
                                }
                              >
                                {transaction.code || "—"}
                              </Button>
                            )
                          ) : (
                            transaction.code || "—"
                          )}
                        </td>
                        <td>
                          {numericPurposes.length > 0 ? (
                            <Form.Select
                              size="sm"
                              value={transaction.purposeId == null ? "" : String(transaction.purposeId)}
                              onChange={(e) => handleUpdatePurpose(transaction, e?.target?.value ?? "")}
                              disabled={
                                updatingPurposeId === transaction.id ||
                                editingSenderId === transaction.id ||
                                updatingSenderId === transaction.id ||
                                editingCodeId === transaction.id ||
                                updatingCodeId === transaction.id
                              }
                              style={{ maxWidth: "180px" }}
                              title={displayMode === "grouped" ? "Edits the first contribution in this group" : "Edit purpose"}
                            >
                              <option value="">Uncategorized</option>
                              {numericPurposes.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name}
                                </option>
                              ))}
                            </Form.Select>
                          ) : (
                            getPurposeName(transaction.purposeId)
                          )}
                        </td>
                        <td className="text-end">
                          {canEditAmountInline(transaction) ? (
                            <Button
                              size="sm"
                              variant="outline-danger"
                              title="Delete this contribution"
                              onClick={() => requestDeleteContribution(transaction)}
                              disabled={
                                deletingId != null ||
                                updatingAmountId === transaction.id ||
                                updatingSenderId === transaction.id ||
                                editingSenderId === transaction.id ||
                                editingCodeId === transaction.id ||
                                updatingCodeId === transaction.id
                              }
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mt-3">
                <small className="text-muted">
                  Showing {listForTable.length === 0 ? 0 : startIndex + 1}
                  {"-"}
                  {Math.min(startIndex + pageSize, listForTable.length)} of {listForTable.length}
                </small>
                <Pagination className="mb-0">
                  <Pagination.Prev
                    disabled={safePage <= 1}
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  />
                  {Array.from({ length: totalPages }, (_, index) => {
                    const pageNumber = index + 1;
                    const isActive = pageNumber === safePage;
                    if (
                      pageNumber !== 1 &&
                      pageNumber !== totalPages &&
                      Math.abs(pageNumber - safePage) > 1
                    ) {
                      return null;
                    }
                    return (
                      <Pagination.Item
                        key={pageNumber}
                        active={isActive}
                        onClick={() => setCurrentPage(pageNumber)}
                      >
                        {pageNumber}
                      </Pagination.Item>
                    );
                  })}
                  <Pagination.Next
                    disabled={safePage >= totalPages}
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  />
                </Pagination>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xs={12} className="mb-4">
          <Card border="light" className="shadow-sm mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Report</h5>
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" size="sm" onClick={() => setShowPreview(true)}>
                  <FontAwesomeIcon icon={faEye} className="me-2" />
                  Preview
                </Button>
                <Button variant="outline-primary" size="sm" onClick={handleCopyReport}>
                  <FontAwesomeIcon icon={faCopy} className="me-2" />
                  {copied ? "Copied" : "Copy for WhatsApp"}
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <p className="small text-muted mb-2">Copy to share in WhatsApp.</p>
              <Form.Group className="mb-3">
                <Form.Label>Message to the group (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={reportHeaderMessage}
                  onChange={(e) => setReportHeaderMessage(e?.target?.value ?? "")}
                  placeholder="e.g. Condolences to the family. Please support the burial arrangements."
                />
                <Form.Text className="text-muted">Appears at the top of the WhatsApp report.</Form.Text>
              </Form.Group>
              <Form.Control as="textarea" rows={10} value={contributionReportText} readOnly className="bg-light" />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {(membersPieSeries.length > 0 || contributionBarData) ? (
        <Row className="g-3 mb-4">
          <Col xs={12}>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowCharts((prev) => !prev)}
              aria-expanded={showCharts}
            >
              {showCharts ? "Hide charts" : "Show charts"}
            </Button>
          </Col>
          {showCharts ? (
            <>
              {membersPieSeries.length > 0 ? (
                <Col md={6}>
                  <Card border="light" className="shadow-sm h-100">
                    <Card.Header className="d-flex align-items-center justify-content-between">
                      <h6 className="mb-0">Member contributions</h6>
                      <span className="text-muted small">
                        {contributedMembers.length}/{groupMembers.length}
                      </span>
                    </Card.Header>
                    <Card.Body className="d-flex flex-column flex-md-row align-items-center justify-content-between small">
                      <div className="ct-contrib-green flex-shrink-0">
                        <CircleChart series={membersPieSeries} />
                      </div>
                      <div className="ms-md-3 mt-3 mt-md-0">
                        <div className="d-flex align-items-center mb-2">
                          <span className="me-2">Contributed</span>
                          <span className="fw-bold">{contributedMembers.length}</span>
                        </div>
                        <div className="d-flex align-items-center">
                          <span className="me-2">Not yet</span>
                          <span className="fw-bold">{Math.max(groupMembers.length - contributedMembers.length, 0)}</span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ) : null}
              {contributionBarData ? (
                <Col md={6}>
                  <Card border="light" className="shadow-sm h-100">
                    <Card.Header className="d-flex align-items-center justify-content-between">
                      <h6 className="mb-0">
                        {group?.frequency === "weekly" ? "Weekly totals" : "Monthly totals"}
                      </h6>
                      <span className="text-muted small">Total contributions</span>
                    </Card.Header>
                    <Card.Body className="small">
                      <div className="ct-contrib-green">
                        <BarChart labels={contributionBarData.labels} series={contributionBarData.series} />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ) : null}
            </>
          ) : null}
        </Row>
      ) : null}

      <Modal show={showDeleteModal} onHide={handleCancelDeleteContribution} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete contribution?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            This will permanently remove this contribution from the ledger. This action cannot be undone.
          </p>
          {transactionPendingDelete ? (
            <p className="small text-muted mb-0">
              <strong>{formatPersonName(transactionPendingDelete.sender) || "—"}</strong>
              {" · "}
              {formatAmount(transactionPendingDelete.amount)}
              {transactionPendingDelete.timestamp || transactionPendingDelete.date
                ? ` · ${formatTableDate(transactionPendingDelete.timestamp || transactionPendingDelete.date)}`
                : ""}
            </p>
          ) : null}
          {deleteError ? <Alert variant="danger" className="mt-3 mb-0 py-2">{deleteError}</Alert> : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDeleteContribution} disabled={deletingId != null}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDeleteContribution} disabled={deletingId != null}>
            {deletingId != null ? "Deleting…" : "Delete permanently"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPreview} onHide={handleClosePreview} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div id="report-print-content" className="p-3 rounded-3 bg-light" style={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
            {contributionReportText}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={handleCopyReport}>
            <FontAwesomeIcon icon={faCopy} className="me-2" />
            {copied ? "Copied" : "Copy for WhatsApp"}
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => {
              const printContent = document.getElementById("report-print-content");
              if (!printContent) return;
              const win = window.open("", "_blank");
              win.document.write(`
                <!DOCTYPE html><html><head><title>Report</title></head>
                <body style="padding:20px;font-family:monospace;white-space:pre-wrap;">${printContent.innerText}</body>
                </html>
              `);
              win.document.close();
              win.focus();
              setTimeout(() => {
                win.print();
                win.close();
              }, 250);
            }}
          >
            <FontAwesomeIcon icon={faPrint} className="me-2" />
            Print
          </Button>
          <Button variant="secondary" onClick={handleClosePreview}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
